import { Goal } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { useLanguage } from '@/i18n/LanguageContext';
import { calculateDaysRemainingStr, getGoalProgress, getGoalStatus } from '@/lib/utils.ts';
import { AlertCircle, CheckCircle2, Clock, MoreHorizontal, Target, XCircle } from 'lucide-react';

// Helper functions
const getStatusIcon = (status: 'onTrack' | 'atRisk' | 'behind') => {
  switch (status) {
    case 'onTrack':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'atRisk':
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    case 'behind':
      return <XCircle className="h-5 w-5 text-red-500" />;
  }
};

const getPriorityColor = (priority: string): string => {
  const colors: Record<string, string> = {
    mandatory: '#D32F2F',
    'nice-to-have': '#1976D2',
  };
  return colors[priority] || '#757575';
};

interface GoalCardProps {
  goal: Goal;
  onViewDetails: (goal: Goal) => void;
  onEdit: (goal: Goal) => void;
  onDelete: (goal: Goal) => void;
}

export const GoalCard = ({ goal, onViewDetails, onEdit, onDelete }: GoalCardProps) => {
  const { t } = useLanguage();
  const status = getGoalStatus(goal);

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent opening details if clicking on the dropdown menu
    if ((e.target as HTMLElement).closest('.dropdown-menu')) {
      return;
    }
    onViewDetails(goal);
  };

  return (
    <Card
      className="cursor-pointer overflow-hidden transition-shadow hover:shadow-md"
      onClick={handleCardClick}
    >
      <div className="flex items-center p-4 pb-0">
        <div className="bg-primary/10 mr-3 rounded p-2">
          <Target className="h-5 w-5" style={{ color: getPriorityColor(goal.priority) }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium" title={goal.name}>
            {goal.name}
          </div>
          <div className="text-muted-foreground text-xs">
            {t(`goals.priorities.${goal.priority}`)}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="dropdown-menu h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="dropdown-menu">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(goal);
              }}
            >
              {t('goals.actions.viewDetails')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEdit(goal);
              }}
            >
              {t('goals.actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-500"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(goal);
              }}
            >
              {t('goals.actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <CardContent className="p-4 pt-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{t('goals.progress')}</span>
            <span className="text-sm font-medium">{getGoalProgress(goal).toFixed(1)}%</span>
          </div>
          <Progress value={getGoalProgress(goal)} className="h-2" />
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <div className="flex items-center">
              {getStatusIcon(status)}
              <span className="ml-1">{t(`goals.status.${status}`)}</span>
            </div>
            {calculateDaysRemainingStr(goal.dueDate) < 0 ? (
              <div className="flex items-center text-red-500">
                <Clock className="mr-1 h-3 w-3" />
                <span>
                  {Math.abs(calculateDaysRemainingStr(goal.dueDate))} {t('goals.daysBehind')}
                </span>
              </div>
            ) : (
              <div className="flex items-center">
                <Clock className="mr-1 h-3 w-3" />
                <span>
                  {calculateDaysRemainingStr(goal.dueDate)} {t('goals.daysRemaining')}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>
              {goal.currentAmount.toLocaleString()} {goal.currency}
            </span>
            <span>
              {goal.targetAmount.toLocaleString()} {goal.currency}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
